import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ConsentFormPage from './components/ConsentFormPage';
import ConsentSuccessPage from './components/ConsentSuccessPage';
import ConsentWithdrawnPage from './components/ConsentWithdrawnPage';
import AcceptInvitationPage from './components/AcceptInvitationPage';
import ProtectedRoute from './components/ProtectedRoute';
import { apiClient, AuthResponse } from './services/api';
import ChangePassword from './components/ChangePassword';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import InactivityWarningModal from './components/Modals/InactivityWarningModal';
import { ScoringConfigProvider } from './contexts/ScoringConfigContext';

type User = AuthResponse['user'];

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const prevUserRef = useRef<User | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      const token = apiClient.getToken();
      if (token) {
        try {
          // Validate token with server and get user data
          const userData = await apiClient.validateToken();
          setUser(userData);
        } catch (error) {
          // Token is invalid, user stays null
          console.log('Token validation failed:', error);
        }
      }
      setIsLoading(false);
    };

    validateToken();
  }, []);

  // Handle token expiry - listen for when token is cleared from localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If auth_token is removed and we still have a user, logout
      if (e.key === 'auth_token' && e.newValue === null && user) {
        console.log('Token expired or removed. Logging out...');
        toast.error('Your session has expired. Please log in again.');
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  const handleLoginSuccess = (userData: User) => {
    setUser(userData);
  };

  const handleLogout = useCallback(async () => {
    // Write to localStorage to trigger logout in all tabs
    localStorage.setItem('forceLogout', Date.now().toString());

    setUser(null);
    await apiClient.logout();

    // Clear inactivity timer localStorage values to prevent them from persisting after logout
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('forceLogout');
  }, []);

  // Initialize inactivity timer (only when user is logged in)
  const {
    isWarningShown,
    remainingTime,
    resetTimer
  } = useInactivityTimer({
    warningThreshold: 28 * 60 * 1000,  // 28 minutes
    logoutThreshold: 30 * 60 * 1000,   // 30 minutes
    onLogout: handleLogout
  });

  // Initialize token refresh (only refresh when user is active)
  useTokenRefresh({
    refreshInterval: 15 * 60 * 1000,   // 15 minutes
    isActive: !!user && !isWarningShown,  // Only refresh if logged in and not in warning state
    onRefreshError: handleLogout
  });

  // Reset inactivity timer when user logs in (only when going from null to logged in)
  useEffect(() => {
    const wasLoggedOut = prevUserRef.current === null;
    const isNowLoggedIn = user !== null;

    if (wasLoggedOut && isNowLoggedIn) {
      // User just logged in, reset the timer to start fresh
      resetTimer();
    }

    // Update ref for next render
    prevUserRef.current = user;
  }, [user, resetTimer]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
      </div>
    );
  }

  return (
    <Router>
      {/* Inactivity Warning Modal */}
      {user && (
        <InactivityWarningModal
          isOpen={isWarningShown}
          remainingSeconds={remainingTime}
          onStayLoggedIn={resetTimer}
          onLogoutNow={handleLogout}
        />
      )}

      <Toaster
        position="top-right"
        toastOptions={{
          // Default options for all toasts
          duration: 4000,
          style: {
            background: '#fff',
            color: '#231f20',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '14px',
            fontWeight: '500',
          },
          // Success toasts
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#69BE28',
              secondary: '#fff',
            },
            style: {
              border: '1px solid #69BE28',
            },
          },
          // Error toasts
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
            style: {
              border: '1px solid #EF4444',
            },
          },
          // Loading toasts
          loading: {
            iconTheme: {
              primary: '#69BE28',
              secondary: '#fff',
            },
          },
        }}
      />
      <div className="App">
        <Routes>
          {/* Public consent routes - no authentication required */}
          <Route path="/consent/:token" element={<ConsentFormPage />} />
          <Route path="/consent/success" element={<ConsentSuccessPage />} />
          <Route path="/consent/withdrawn" element={<ConsentWithdrawnPage />} />

          {/* Public invitation acceptance route */}
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

          {/* Change password route - semi-protected (requires token but blocks dashboard) */}
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Public route - Login */}
          <Route
            path="/login"
            element={
              user ? <Navigate to="/" replace /> :
                <Login onLoginSuccess={handleLoginSuccess} />
            }
          />

          {/* Protected routes - Dashboard and all sub-pages */}
          <Route
            path="/*"
            element={
              <ProtectedRoute user={user}>
                <ScoringConfigProvider>
                  <Dashboard user={user} onLogout={handleLogout} />
                </ScoringConfigProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;